import React from 'react';
import type { Proposal } from '../types';

interface ProposalCardProps {
  proposal: Proposal;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onRestore?: () => void;
  /** Field names to hide from display (e.g. single-option fields). */
  hiddenFields?: Set<string>;
  /** Default slot duration in minutes (for showing session length). */
  defaultSlotMinutes?: number;
  /** Whether this proposal belongs to a session (and which one). */
  sessionInfo?: { sessionTitle: string; otherProposalTitles: string[] } | null;
  /** Whether the current user voted / showed interest for this proposal. */
  voted?: boolean;
}

export function ProposalCard({
  proposal,
  onClick,
  onEdit,
  onDelete,
  onRestore,
  hiddenFields,
  defaultSlotMinutes,
  sessionInfo,
  voted,
}: ProposalCardProps) {
  const peopleLabel = proposal.people?.length
    ? proposal.people.map((p) => p.nickname).join(', ')
    : proposal.createdByNickname;

  const isDeleted = !!proposal.deletedAt;

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  const handleRestore = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRestore?.();
  };

  // Compute duration label from preferredSlotSpan
  const durationLabel = (() => {
    if (!proposal.preferredSlotSpan || !defaultSlotMinutes) return null;
    const mins = proposal.preferredSlotSpan * defaultSlotMinutes;
    return `${mins} min`;
  })();

  return (
    <article
      className={`card card-hover ${isDeleted ? 'opacity-50 border-dashed' : ''}`}
      onClick={isDeleted ? undefined : onClick}
      tabIndex={onClick && !isDeleted ? 0 : undefined}
      role={onClick && !isDeleted ? 'button' : undefined}
      aria-label={`Session card: ${proposal.title} by ${peopleLabel}${isDeleted ? ' (deleted)' : ''}`}
    >
      {/* Deleted banner */}
      {isDeleted && (
        <div className="bg-red-50 -mx-4 -mt-4 px-4 py-2 mb-3 rounded-t-2xl border-b border-red-200 flex items-center justify-between gap-2">
          <p className="text-xs font-semibold text-red-700">This card was removed</p>
          {onRestore && (
            <button
              type="button"
              onClick={handleRestore}
              className="btn btn-sm text-xs text-red-700 hover:text-red-900 underline"
            >
              Restore
            </button>
          )}
        </div>
      )}

      {/* Session grouping banner */}
      {sessionInfo && !isDeleted && (
        <div className="bg-primary-50 -mx-4 -mt-4 px-4 py-2 mb-3 rounded-t-2xl border-b border-primary-100">
          <p className="text-xs font-semibold text-primary-700">
            Session: {sessionInfo.sessionTitle}
          </p>
          {sessionInfo.otherProposalTitles.length > 0 && (
            <p className="text-xs text-primary-600 mt-0.5">
              Combined with: {sessionInfo.otherProposalTitles.join(', ')}
            </p>
          )}
        </div>
      )}

      <div className="flex-1 min-w-0">
        {/* Title row: ID + title left-aligned, edit button right */}
        <div className="flex items-start gap-2">
          <div className="flex items-baseline gap-1.5 min-w-0 flex-1">
            {proposal.displayId != null && (
              <span className="text-sm font-mono text-primary-600 font-semibold flex-shrink-0" title="Proposal number">
                #{proposal.displayId}
              </span>
            )}
            <h3 className="font-semibold text-gray-900 line-clamp-2 min-w-0">
              {proposal.title}
            </h3>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {onEdit && !isDeleted && (
              <button
                type="button"
                onClick={handleEdit}
                className="btn btn-ghost btn-sm text-gray-500 hover:text-primary-600"
                aria-label="Edit session card"
              >
                Edit
              </button>
            )}
            {onDelete && !isDeleted && (
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-ghost btn-sm text-gray-400 hover:text-red-600"
                aria-label="Remove session card"
                title="Remove session card"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Byline */}
        <p className="text-sm text-gray-600 mt-1">
          by {peopleLabel}
        </p>

        {/* Single row: duration · format · language · level */}
        {(() => {
          const chips: string[] = [];
          if (durationLabel) chips.push(durationLabel);
          if (proposal.fields) {
            for (const k of ['format', 'language', 'level'] as const) {
              if (proposal.fields[k]) chips.push(proposal.fields[k]);
            }
          }
          return chips.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {chips.map((chip) => (
                <span key={chip} className="inline-block text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                  {chip}
                </span>
              ))}
            </div>
          ) : null;
        })()}

        {/* Description */}
        {proposal.description && (
          <p className="text-sm text-gray-500 mt-2 line-clamp-2">
            {proposal.description}
          </p>
        )}

        {/* Tags at bottom */}
        {proposal.fields?.tags && !hiddenFields?.has('tags') && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(typeof proposal.fields.tags === 'string'
              ? proposal.fields.tags.split(',').map((s: string) => s.trim()).filter(Boolean)
              : Array.isArray(proposal.fields.tags)
                ? proposal.fields.tags
                : []
            ).map((tag: string) => (
              <span key={tag} className="badge badge-gray text-xs">
                {tag}
              </span>
            ))}
          </div>
        )}
        {/* Other custom fields as badges */}
        {proposal.fields && Object.entries(proposal.fields).some(([k, v]) => v && !['language', 'format', 'level', 'tags'].includes(k) && !hiddenFields?.has(k)) && (
          <div className="flex flex-wrap gap-2 mt-2">
            {Object.entries(proposal.fields).map(([key, value]) =>
              value && !['language', 'format', 'level', 'tags'].includes(key) && !hiddenFields?.has(key) ? (
                <span key={key} className="badge badge-gray">
                  {String(value)}
                </span>
              ) : null
            )}
          </div>
        )}

        {/* Bottom row: vote count + voted indicator */}
        <div className="flex items-center gap-3 mt-2">
          {proposal.voteCount > 0 && (
            <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: 'var(--color-brand-gold)' }}>
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
              <span className="tabular-nums">{proposal.voteCount}</span>
              <span className="font-normal text-gray-500">interested</span>
            </span>
          )}
          {voted && (
            <span className="voted-badge" title="You showed interest in this session card">
              <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              You showed interest
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
